import React, { useState, useMemo } from 'react';
import boliData from '../data/db.json';
import InfoTooltip from './InfoTooltip';

export default function Step1Selection({ selectedDiseases, setSelectedDiseases, onNext }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [openCategories, setOpenCategories] = useState({});

  const filteredDiseases = boliData.filter(boala => 
    boala.nume.toLowerCase().includes(searchTerm.toLowerCase()) || 
    boala.categorie.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (boala.subcategorie && boala.subcategorie.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Group diseases by categorie
  const grouped = useMemo(() => {
    const groups = {};
    filteredDiseases.forEach(boala => {
      if (!groups[boala.categorie]) groups[boala.categorie] = [];
      groups[boala.categorie].push(boala);
    });
    return groups;
  }, [filteredDiseases]);

  const toggleDisease = (boala) => {
    if (selectedDiseases.find(d => d.id_boala === boala.id_boala)) {
      setSelectedDiseases(selectedDiseases.filter(d => d.id_boala !== boala.id_boala));
    } else {
      setSelectedDiseases([...selectedDiseases, boala]);
    }
  };

  const toggleCategory = (cat) => {
    setOpenCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const selectedCount = (cat) => {
    const catDiseases = boliData.filter(b => b.categorie === cat);
    return catDiseases.filter(b => selectedDiseases.find(s => s.id_boala === b.id_boala)).length;
  };

  // When searching, auto-expand all categories
  const isSearching = searchTerm.length > 0;

  return (
    <div>
      <h2 style={{ color: '#2c3e50', marginBottom: '4px' }}>Pasul 1: Selecția Afecțiunilor</h2>
      <p style={{ color: '#7f8c8d', marginTop: 0, marginBottom: '20px', fontSize: '14px' }}>
        Căutați și bifați afecțiunile pentru care se solicită evaluarea handicapului.
        <InfoTooltip 
          title="Cum funcționează selecția?" 
          text={`La acest pas trebuie să selectați TOATE afecțiunile (bolile) pe care le are persoana evaluată și care se regăsesc în Anexa 1 a Ordinului MM 2300/2025.\n\n📌 IMPORTANT:\n• Puteți selecta mai multe afecțiuni simultan (handicap asociat).\n• Gradul final se stabilește în funcție de severitatea celei predominante.\n• Dacă nu găsiți afecțiunea, folosiți caseta de căutare.\n• Categoriile sunt colapsabile – apăsați pe titlul capitolului pentru a-l deschide.`} 
        />
      </p>

      <input 
        type="text" 
        placeholder="🔍 Căutare după nume boală, capitol sau cuvânt cheie..." 
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="search-input"
        style={{ marginBottom: '20px' }}
      />

      {selectedDiseases.length > 0 && (
        <div style={{ 
          padding: '12px 16px', 
          background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)', 
          borderRadius: '10px', 
          marginBottom: '16px',
          fontSize: '14px',
          color: '#2e7d32',
          fontWeight: 500
        }}>
          ✅ {selectedDiseases.length} afecțiune(i) selectate: {selectedDiseases.map(d => d.nume).join(', ')}
        </div>
      )}

      <div style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid #e0e6ed', borderRadius: '12px', padding: '8px' }}>
        {Object.keys(grouped).length === 0 && (
          <div style={{ padding: '30px', textAlign: 'center', color: '#999' }}>
            Nicio afecțiune găsită pentru „{searchTerm}".
          </div>
        )}

        {Object.keys(grouped).map(categorie => {
          const isOpen = isSearching || openCategories[categorie];
          const count = selectedCount(categorie);

          return (
            <div key={categorie} style={{ marginBottom: '4px' }}>
              <div 
                className="accordion-header"
                onClick={() => toggleCategory(categorie)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                  <span>{categorie}</span>
                  {count > 0 && <span className="badge">{count}</span>}
                </div>
                <span style={{ fontSize: '12px', color: '#888' }}>{grouped[categorie].length} afecțiuni</span>
              </div>

              {isOpen && (
                <div className="accordion-body">
                  {(() => {
                    // Group by subcategorie if present
                    const diseases = grouped[categorie];
                    const hasSubcats = diseases.some(b => b.subcategorie);
                    
                    if (!hasSubcats) {
                      // No sub-categories, render flat list
                      return diseases.map(boala => {
                        const isSelected = selectedDiseases.find(d => d.id_boala === boala.id_boala);
                        return (
                          <div 
                            key={boala.id_boala} 
                            className={`card ${isSelected ? 'card-selected' : ''}`}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '14px 16px' }}
                            onClick={() => toggleDisease(boala)}
                          >
                            <input type="checkbox" checked={!!isSelected} onChange={() => {}} 
                              style={{ width: '18px', height: '18px', marginRight: '14px', accentColor: '#0056b3', flexShrink: 0 }}
                            />
                            <span style={{ fontSize: '14px', color: isSelected ? '#0056b3' : '#333', fontWeight: isSelected ? 600 : 400 }}>
                              {boala.nume}
                            </span>
                          </div>
                        );
                      });
                    }
                    
                    // Group by subcategorie
                    const subGroups = {};
                    const noSubcat = [];
                    diseases.forEach(b => {
                      if (b.subcategorie) {
                        if (!subGroups[b.subcategorie]) subGroups[b.subcategorie] = [];
                        subGroups[b.subcategorie].push(b);
                      } else {
                        noSubcat.push(b);
                      }
                    });
                    
                    return (
                      <>
                        {Object.keys(subGroups).map(sub => (
                          <div key={sub}>
                            <div style={{ 
                              padding: '10px 16px', 
                              fontSize: '13px', 
                              fontWeight: 700, 
                              color: '#0056b3', 
                              background: '#e8f0fe',
                              borderBottom: '1px solid #d0ddef',
                              letterSpacing: '0.3px'
                            }}>
                              {sub}
                            </div>
                            {subGroups[sub].map(boala => {
                              const isSelected = selectedDiseases.find(d => d.id_boala === boala.id_boala);
                              return (
                                <div 
                                  key={boala.id_boala} 
                                  className={`card ${isSelected ? 'card-selected' : ''}`}
                                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '14px 16px', paddingLeft: '28px' }}
                                  onClick={() => toggleDisease(boala)}
                                >
                                  <input type="checkbox" checked={!!isSelected} onChange={() => {}} 
                                    style={{ width: '18px', height: '18px', marginRight: '14px', accentColor: '#0056b3', flexShrink: 0 }}
                                  />
                                  <span style={{ fontSize: '14px', color: isSelected ? '#0056b3' : '#333', fontWeight: isSelected ? 600 : 400 }}>
                                    {boala.nume}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                        {noSubcat.map(boala => {
                          const isSelected = selectedDiseases.find(d => d.id_boala === boala.id_boala);
                          return (
                            <div 
                              key={boala.id_boala} 
                              className={`card ${isSelected ? 'card-selected' : ''}`}
                              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '14px 16px' }}
                              onClick={() => toggleDisease(boala)}
                            >
                              <input type="checkbox" checked={!!isSelected} onChange={() => {}} 
                                style={{ width: '18px', height: '18px', marginRight: '14px', accentColor: '#0056b3', flexShrink: 0 }}
                              />
                              <span style={{ fontSize: '14px', color: isSelected ? '#0056b3' : '#333', fontWeight: isSelected ? 600 : 400 }}>
                                {boala.nume}
                              </span>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: '24px', textAlign: 'right' }} className="no-print">
        <button 
          onClick={onNext} 
          disabled={selectedDiseases.length === 0}
          className={`btn ${selectedDiseases.length > 0 ? 'btn-success' : 'btn-disabled'}`}
        >
          Următorul Pas →
        </button>
      </div>
    </div>
  );
}
